import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, User, UserPlus, Zap, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import BrandLogo from '@/components/BrandLogo';

const passwordRules = [
  { label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { label: 'One lowercase letter', test: (p) => /[a-z]/.test(p) },
  { label: 'One number', test: (p) => /[0-9]/.test(p) },
  { label: 'One special character (!@#$...)', test: (p) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(p) },
];

export default function SignupPage() {
  useDocumentTitle('Create Account');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [signupComplete, setSignupComplete] = useState(false);
  const [resending, setResending] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const passedRules = passwordRules.filter((r) => r.test(password));
  const strengthPercent = password.length === 0 ? 0 : (passedRules.length / passwordRules.length) * 100;
  const strengthColor = strengthPercent <= 40 ? 'bg-[#2563FF]' : strengthPercent <= 80 ? 'bg-yellow-500' : 'bg-green-500';

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name || !email || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (passedRules.length < passwordRules.length) {
      toast.error('Password does not meet all requirements');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/signup`, {
        email,
        password,
        name
      });
      
      if (response.data.email_verification_required) {
        setSignupComplete(true);
        toast.success('Account created! Check your email to verify.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification?email=${encodeURIComponent(email)}`);
      toast.success('Verification email sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center justify-center">
            <BrandLogo size="md" />
          </Link>
          <p className="font-body text-[#64748B] mt-4">
            {signupComplete ? 'Verify your email' : 'Create your DJ account'}
          </p>
        </div>

        {/* Content */}
        <div className="glass-pane p-8">
          {signupComplete ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-4"
            >
              <div className="w-16 h-16 bg-green-950/30 border border-green-500/50 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="font-heading text-xl text-[#0F172A] mb-2">Check Your Email</h2>
              <p className="font-body text-[#64748B] text-sm mb-4">
                We've sent a verification link to:
              </p>
              <p className="font-mono text-[#0F172A] mb-6">{email}</p>
              <p className="font-body text-[#64748B] text-xs mb-6">
                Click the link in the email to activate your account. Check your spam folder if you don't see it.
              </p>
              
              <div className="space-y-3">
                <Button
                  onClick={handleResendVerification}
                  disabled={resending}
                  variant="outline"
                  className="w-full border-[#E2E8F0] text-[#64748B] hover:text-[#0F172A]"
                  data-testid="resend-verification"
                >
                  {resending ? (
                    <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <Mail className="w-4 h-4 mr-2" />
                  )}
                  Resend Verification Email
                </Button>
                
                <Link
                  to="/login"
                  className="block text-center font-body text-[#64748B] hover:text-[#0F172A] text-sm"
                >
                  Back to sign in
                </Link>
              </div>
            </motion.div>
          ) : (
            <>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block font-mono text-xs text-[#64748B] mb-2 uppercase">
                    Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                    <Input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your DJ name"
                      className="pl-10 bg-white border-[#E2E8F0] text-[#0F172A]"
                      data-testid="signup-name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-mono text-xs text-[#64748B] mb-2 uppercase">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="pl-10 bg-white border-[#E2E8F0] text-[#0F172A]"
                      data-testid="signup-email"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-mono text-xs text-[#64748B] mb-2 uppercase">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 8 characters"
                      className="pl-10 pr-10 bg-white border-[#E2E8F0] text-[#0F172A]"
                      data-testid="signup-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#334155] transition-colors"
                      data-testid="signup-toggle-password"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {/* Password Strength */}
                  {password.length > 0 && (
                    <div className="mt-2 space-y-2" data-testid="password-strength-indicator">
                      <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                        <div className={`h-full ${strengthColor} transition-all duration-300`} style={{ width: `${strengthPercent}%` }} />
                      </div>
                      <div className="grid grid-cols-2 gap-1">
                        {passwordRules.map((rule, i) => (
                          <span key={i} className={`text-[10px] font-mono ${rule.test(password) ? 'text-green-500' : 'text-[#94A3B8]'}`}>
                            {rule.test(password) ? '\u2713' : '\u2717'} {rule.label}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block font-mono text-xs text-[#64748B] mb-2 uppercase">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                    <Input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm your password"
                      className="pl-10 pr-10 bg-white border-[#E2E8F0] text-[#0F172A]"
                      data-testid="signup-confirm-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#334155] transition-colors"
                      data-testid="signup-toggle-confirm-password"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary h-12 font-heading"
                  data-testid="signup-submit"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <UserPlus className="w-5 h-5 mr-2" />
                      CREATE ACCOUNT
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="font-body text-[#64748B]">
                  Already have an account?{' '}
                  <Link
                    to="/login"
                    className="text-[#2563FF] hover:text-[#3B82F6] font-medium"
                    data-testid="login-link"
                  >
                    Sign in
                  </Link>
                </p>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
